import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CATEGORIES = [
  ["general", "General Service"], ["technology", "Technology / Infrastructure"],
  ["website", "Website / Web Development"], ["code", "Code / Software"],
  ["managed", "Managed Digital Service"], ["content", "Content Updates"],
  ["email", "Email & Communications"], ["administrative", "Administrative Support"],
  ["custom", "Custom / Other"],
];
const inputCls = "bg-white border-[#E2EBF0]";

export default function ServiceDetailDialog({ service, open, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (service) setF({
      name: service.name || "", description: service.description || "",
      presentation_type: service.presentation_type || "general",
      long_description: service.long_description || "", scope: service.scope || "",
      deliverables: service.deliverables || "", benefits: service.benefits || "",
      support_coverage: service.support_coverage || "", client_notes: service.client_notes || "",
      notes: service.notes || "", start_date: service.start_date || "", contract_end_date: service.contract_end_date || "",
    });
  }, [service]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/services/${service.service_id}`, f);
      toast.success("Service details saved");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (!service) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0D3B2D]">Edit service details</DialogTitle>
          <DialogDescription className="text-[#4A635A]">Content and presentation only — price, billing and Stripe are never changed here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="svc-edit-name" className={inputCls} /></div>
          <div><Label>Category</Label>
            <select value={f.presentation_type} onChange={(e) => set("presentation_type", e.target.value)}
              data-testid="svc-edit-category" className={`${inputCls} w-full h-10 rounded-md border px-3 text-sm text-[#0D3B2D]`}>
              {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><Label>Short description (client-facing)</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="svc-edit-desc" className={inputCls} /></div>
          <div><Label>Detailed description</Label><Textarea value={f.long_description} onChange={(e) => set("long_description", e.target.value)} rows={3} data-testid="svc-edit-long" className={inputCls} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Included deliverables</Label><Textarea value={f.deliverables} onChange={(e) => set("deliverables", e.target.value)} rows={2} className={inputCls} /></div>
            <div><Label>Benefits</Label><Textarea value={f.benefits} onChange={(e) => set("benefits", e.target.value)} rows={2} className={inputCls} /></div>
            <div><Label>Scope</Label><Textarea value={f.scope} onChange={(e) => set("scope", e.target.value)} rows={2} className={inputCls} /></div>
            <div><Label>Maintenance / support scope</Label><Textarea value={f.support_coverage} onChange={(e) => set("support_coverage", e.target.value)} rows={2} className={inputCls} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Contract start</Label><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} /></div>
            <div><Label>Renewal / paid-through</Label><Input type="date" value={f.contract_end_date} onChange={(e) => set("contract_end_date", e.target.value)} className={inputCls} /></div>
          </div>
          <div><Label>Client-facing notes</Label><Textarea value={f.client_notes} onChange={(e) => set("client_notes", e.target.value)} rows={2} className={inputCls} /></div>
          <div><Label>Internal notes (hidden from client)</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="svc-edit-notes" className={inputCls} /></div>
          <Button onClick={save} disabled={saving} data-testid="svc-edit-save" className="w-full bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">{saving ? "Saving…" : "Save details"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
